// The rider's goal, whatever it is: a name, a date, its days and its biggest day. Measured from the
// rider's own GPX files where they have them (the stages), typed where they don't, and "just riding"
// where there's no goal at all. Every screen that used to say "Paris" asks here instead.
import type { GoalKind, State } from "./types";

export const GOAL_KINDS: { kind: GoalKind; label: string }[] = [
  { kind: "event", label: "A big ride or event" },
  { kind: "sportive", label: "A sportive" },
  { kind: "race", label: "A race or a race season" },
  { kind: "trip", label: "A trip" },
  { kind: "first100", label: "A first 100 km" },
  { kind: "club", label: "Keeping up on the club ride" },
  { kind: "riding", label: "Just riding" },
];

type S = Pick<State, "stages" | "goal">;
const riding = (s: S) => s.goal?.kind === "riding";

/** The goal's name, or none for just riding. */
export function goalName(s: S): string | null {
  if (riding(s)) return null;
  if (s.goal?.name) return s.goal.name;
  const a = s.stages[0], b = s.stages[s.stages.length - 1];
  if (a && b) return `${a.from} to ${b.to}`;
  return s.goal?.kind === "first100" ? "A first 100 km" : s.goal?.kind === "club" ? "The club ride" : null;
}

/** Its first day, or none. */
export function goalDate(s: S): string | null {
  if (riding(s)) return null;
  return s.goal?.date ?? s.stages[0]?.date ?? null;
}

/** How many days it runs. */
export const goalDays = (s: S): number => (riding(s) ? 0 : s.stages.length || s.goal?.days || (s.goal ? 1 : 0));

/** The day the plan builds towards: the longest stage, or what was typed. Km and climb may be unknown. */
export function keyDay(s: S): { label: string; km: number | null; climb: number | null; measured: boolean } | null {
  if (riding(s)) return null;
  if (s.stages.length) {
    const st = s.stages.reduce((a, b) => ((b.km.v ?? 0) > (a.km.v ?? 0) ? b : a));
    return { label: s.stages.length > 1 ? `Day ${st.day}` : "The goal", km: st.km.v, climb: Math.max(...s.stages.map((x) => x.gain.v ?? 0)) || null, measured: true };
  }
  if (!s.goal) return null;
  const km = s.goal.km ?? (s.goal.kind === "first100" ? 100 : null);
  return { label: goalDays(s) > 1 ? "Its biggest day" : "The goal", km, climb: s.goal.climb ?? null, measured: false };
}
